import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '@common/components';
import { InfoIcon } from '@common/components/icons';
import { SelectMulti } from '@common/components/SelectMulti';
import {
  isTimestampColumn,
  getTimestampColumns,
  getValueColumns,
} from '@reports/utils/dashboard/columnUtils';
import {
  DATA_SOURCE_TYPES,
  widgetShowsField,
} from './constants';
import * as styles from './Step3ChartConfig.module.css';
import * as wizardStyles from './styles.module.css';

function Step3ChartConfig({ widgetData, onConfigChange, onDataSourceConfigChange, validationErrors, availableColumns }) {
  const { t } = useTranslation();

  const widgetType = widgetData.type;
  const { config } = widgetData;

  // Get dataSources array from config (memoized to prevent dependency issues)
  const dataSources = useMemo(() => config.dataSources || [], [config.dataSources]);

  // Check if metric_values is in the dataSources
  const metricValuesSource = useMemo(
    () => dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.METRIC_VALUES),
    [dataSources],
  );
  const hasMetricValues = !!metricValuesSource;

  // Check if custom_query is in the dataSources
  const customQuerySource = useMemo(
    () => dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY),
    [dataSources],
  );
  const hasCustomQuery = !!customQuerySource;

  // Get table data sources
  const tableSources = useMemo(
    () => dataSources.filter((ds) => ds.type === DATA_SOURCE_TYPES.TABLE),
    [dataSources],
  );
  const selectedTables = tableSources.map((ds) => ds.id);

  // Get config from the first table, custom_query, or metric_values data source
  const dsConfig = useMemo(() => {
    const tableSource = dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.TABLE);
    const customSource = dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.CUSTOM_QUERY);
    const metricSource = dataSources.find((ds) => ds.type === DATA_SOURCE_TYPES.METRIC_VALUES);
    if (tableSource) return tableSource.config || {};
    if (customSource) return customSource.config || {};
    if (metricSource) return metricSource.config || {};
    return {};
  }, [dataSources]);

  // Determine which source type to use for config updates
  const activeSourceType = useMemo(() => {
    if (tableSources.length > 0) return DATA_SOURCE_TYPES.TABLE;
    if (hasCustomQuery) return DATA_SOURCE_TYPES.CUSTOM_QUERY;
    if (hasMetricValues) return DATA_SOURCE_TYPES.METRIC_VALUES;
    return DATA_SOURCE_TYPES.TABLE;
  }, [tableSources.length, hasCustomQuery, hasMetricValues]);

  // Helper to update config inside the appropriate dataSource
  const handleDataSourceConfigChange = useCallback(
    (field, value, sourceType = DATA_SOURCE_TYPES.TABLE) => {
      if (onDataSourceConfigChange) {
        onDataSourceConfigChange(field, value, sourceType);
      }
    },
    [onDataSourceConfigChange],
  );

  // Group columns by table/data source
  const columnsByTable = useMemo(() => {
    const grouped = {};
    if (availableColumns) {
      availableColumns.forEach((col) => {
        const tableName = col.table || (col.name.includes('.') ? col.name.split('.')[0] : 'default');
        if (!grouped[tableName]) {
          grouped[tableName] = [];
        }
        grouped[tableName].push(col);
      });
    }
    return grouped;
  }, [availableColumns]);

  // Check if this is a line chart (special handling for line charts)
  const isLineChart = widgetType === 'line';

  // Check if we have a data source that needs column configuration
  const hasColumnBasedSource = selectedTables.length > 0 || hasCustomQuery;

  // Determine what fields to show based on widget type (using shared config)
  const showChartBy = hasColumnBasedSource && widgetShowsField(widgetType, 'chartBy');
  const showGroupBy = hasColumnBasedSource && widgetShowsField(widgetType, 'groupBy');
  const showAggregation = hasColumnBasedSource && widgetShowsField(widgetType, 'aggregation');
  const showCategoryLimit = hasColumnBasedSource && widgetShowsField(widgetType, 'categoryLimit');
  const showValueColumn = hasColumnBasedSource && widgetShowsField(widgetType, 'valueColumn');
  const showDisplayName = widgetShowsField(widgetType, 'displayName');
  const showSelectedColumns = hasColumnBasedSource && widgetShowsField(widgetType, 'selectedColumns');

  // Get chip label for data requirement badges (for non-line charts)
  const getRequirementLabel = (field) => {
    if (field === 'chartBy') {
      const chartByCol = availableColumns?.find((col) => col.name === dsConfig.chartBy);
      return chartByCol ? chartByCol.type : 'String';
    }
    return '';
  };

  // Handle table columns selection change - update inside dataSource config
  const handleColumnsChange = useCallback(
    (selectedColumns) => {
      handleDataSourceConfigChange('selectedColumns', selectedColumns, activeSourceType);
    },
    [handleDataSourceConfigChange, activeSourceType],
  );

  // Prepare column options for SelectMulti
  const columnOptions = useMemo(() => {
    if (!availableColumns) return [];
    return availableColumns.map((col) => ({
      value: col.name,
      label: `${col.name} (${col.type})`,
    }));
  }, [availableColumns]);

  // Get timestamp columns for a specific table by name
  const getTimestampColumnsByTable = useCallback(
    (tableName) => {
      const tableColumns = columnsByTable[tableName] || [];
      return tableColumns.filter(isTimestampColumn);
    },
    [columnsByTable],
  );

  // Check if groupBy column is a timestamp type
  const isGroupByTimestamp = useCallback(() => {
    if (!dsConfig.groupBy || !availableColumns) return false;
    const groupByCol = availableColumns.find((col) => col.name === dsConfig.groupBy);
    return isTimestampColumn(groupByCol);
  }, [dsConfig.groupBy, availableColumns]);

  // Get chart settings title based on widget type
  const getChartSettingsTitle = () => {
    const labels = {
      pie: 'Pie Chart Settings',
      donut: 'Donut Chart Settings',
      bar: 'Bar Chart Settings',
      'grouped-bar': 'Grouped Bar Chart Settings',
      'stacked-bar': 'Stacked Bar Chart Settings',
      line: 'Line Chart Settings',
      metric: 'KPI Settings',
      table: 'Table Settings',
    };
    return labels[widgetType] || 'Chart Settings';
  };

  // Get configuration description based on widget type
  const getConfigDescription = () => {
    const descriptions = {
      pie: t('dashboard.widgetWizard.step3.configureDescriptionPie'),
      donut: t('dashboard.widgetWizard.step3.configureDescriptionDonut'),
      bar: t('dashboard.widgetWizard.step3.configureDescriptionBar'),
      'grouped-bar': t('dashboard.widgetWizard.step3.configureDescriptionGroupedBar'),
      'stacked-bar': t('dashboard.widgetWizard.step3.configureDescriptionStackedBar'),
      line: t('dashboard.widgetWizard.step3.configureDescriptionLine'),
      metric: t('dashboard.widgetWizard.step3.configureDescriptionKPI'),
      table: t('dashboard.widgetWizard.step3.configureDescriptionTable'),
    };
    return descriptions[widgetType] || t('dashboard.widgetWizard.step3.configureDescription');
  };

  // Collect all errors for this step
  const errorMessages = [];
  if (validationErrors.chartBy) errorMessages.push(validationErrors.chartBy);
  if (validationErrors.groupBy) errorMessages.push(validationErrors.groupBy);
  if (validationErrors.aggregation) errorMessages.push(validationErrors.aggregation);

  return (
    <div className={styles.container}>
      <h2 className={wizardStyles.stepTitle}>{t('dashboard.widgetWizard.step3.title')}</h2>

      {/* Error Box */}
      <ErrorBox errors={errorMessages} />

      {/* Show configuration section for data sources */}
      {(hasMetricValues || hasColumnBasedSource) && (
        <div className={styles.configLayout}>
          {/* Left: Chart Configuration */}
          <div className={styles.chartConfig}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>{getChartSettingsTitle()}</h3>
            </div>

            <p className={styles.configDescription}>{getConfigDescription()}</p>

            <div className={styles.configFields}>
              {/* Chart By - for non-line charts only */}
              {showChartBy && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="chartBy">
                    {t('dashboard.widgetWizard.step3.chartBy')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.chartByHelp')}</div>
                  <select
                    id="chartBy"
                    className={styles.select}
                    value={dsConfig.chartBy || ''}
                    onChange={(e) => handleDataSourceConfigChange('chartBy', e.target.value, activeSourceType)}
                  >
                    <option value="">{t('dashboard.widgetWizard.step3.selectColumn')}</option>
                    {availableColumns?.map((col) => (
                      <option key={col.name} value={col.name}>
                        {col.name} ({col.type})
                      </option>
                    ))}
                  </select>
                  <div className={styles.dataRequirement}>
                    {t('dashboard.widgetWizard.step3.dataRequirement')}:
                    <span className={styles.requirementBadge}>{getRequirementLabel('chartBy')}</span>
                  </div>
                </div>
              )}

              {/* Group By - for stacked-bar and grouped-bar charts */}
              {showGroupBy && !isLineChart && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="groupBy">
                    {t('dashboard.widgetWizard.step3.groupBy')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.groupByHelp')}</div>
                  <select
                    id="groupBy"
                    className={styles.select}
                    value={dsConfig.groupBy || ''}
                    onChange={(e) => handleDataSourceConfigChange('groupBy', e.target.value, activeSourceType)}
                  >
                    <option value="">{t('dashboard.widgetWizard.step3.selectColumn')}</option>
                    {availableColumns?.map((col) => (
                      <option key={col.name} value={col.name}>
                        {col.name} ({col.type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Metric Values Configuration - rendered as tableConfigSection (line charts only) */}
              {hasMetricValues && metricValuesSource && (
                <div className={styles.tableConfigSection}>
                  <h4 className={styles.tableConfigTitle}>{t('dashboard.widgetWizard.step3.metricValuesTitle')}</h4>

                  {/* Group By (metrics or hosts) */}
                  <div className={styles.configField}>
                    <label className={styles.fieldLabel} htmlFor="groupBy">
                      {t('dashboard.widgetWizard.step3.groupBy')}
                    </label>
                    <div className={styles.fieldDescription}>
                      {t('dashboard.widgetWizard.step3.metricValuesGroupByHelp')}
                    </div>
                    <select
                      id="groupBy"
                      className={styles.select}
                      value={metricValuesSource.config?.groupBy || 'metrics'}
                      onChange={(e) => handleDataSourceConfigChange('groupBy', e.target.value, DATA_SOURCE_TYPES.METRIC_VALUES)}
                    >
                      <option value="metrics">{t('dashboard.widgetWizard.step2.metricValues.entityTypeMetrics')}</option>
                      <option value="hosts">{t('dashboard.widgetWizard.step2.metricValues.entityTypeHosts')}</option>
                    </select>
                  </div>

                  {/* Time Grouping */}
                  <div className={styles.configField}>
                    <label className={styles.fieldLabel} htmlFor="timeGrouping">
                      {t('dashboard.widgetWizard.step2.metricValues.timeGrouping')}
                    </label>
                    <div className={styles.fieldDescription}>
                      {t('dashboard.widgetWizard.step3.timeGroupingHelp')}
                    </div>
                    <select
                      id="timeGrouping"
                      className={styles.select}
                      value={metricValuesSource.config?.aggregateLiteral || '5m'}
                      onChange={(e) => handleDataSourceConfigChange('aggregateLiteral', e.target.value, DATA_SOURCE_TYPES.METRIC_VALUES)}
                    >
                      <option value="5m">5 {t('dashboard.widgetWizard.step2.metricValues.minutes')}</option>
                      <option value="15m">15 {t('dashboard.widgetWizard.step2.metricValues.minutes')}</option>
                      <option value="30m">30 {t('dashboard.widgetWizard.step2.metricValues.minutes')}</option>
                      <option value="1h">1 {t('dashboard.widgetWizard.step2.metricValues.hour')}</option>
                      <option value="6h">6 {t('dashboard.widgetWizard.step2.metricValues.hours')}</option>
                      <option value="12h">12 {t('dashboard.widgetWizard.step2.metricValues.hours')}</option>
                      <option value="1d">1 {t('dashboard.widgetWizard.step2.metricValues.day')}</option>
                    </select>
                  </div>

                  {isLineChart && (
                    <div className={styles.bucketSizeInfo} role="note">
                      <span className={styles.bucketSizeIcon} aria-hidden="true">
                        <InfoIcon />
                      </span>
                      <p className={styles.bucketSizeText}>
                        {t('dashboard.widgetWizard.step3.lineChartTimeBucketNotice')}
                      </p>
                    </div>
                  )}

                  {/* Aggregation Function */}
                  <div className={styles.configField}>
                    <label className={styles.fieldLabel} htmlFor="aggregationFunction">
                      {t('dashboard.widgetWizard.step2.metricValues.aggregationFunction')}
                    </label>
                    <div className={styles.fieldDescription}>
                      {t('dashboard.widgetWizard.step3.aggregationFunctionHelp')}
                    </div>
                    <select
                      id="aggregationFunction"
                      className={styles.select}
                      value={metricValuesSource.config?.aggregateFunction || 'mean'}
                      onChange={(e) => handleDataSourceConfigChange('aggregateFunction', e.target.value, DATA_SOURCE_TYPES.METRIC_VALUES)}
                    >
                      <option value="mean">{t('dashboard.widgetWizard.step2.metricValues.mean')}</option>
                      <option value="sum">{t('dashboard.widgetWizard.step2.metricValues.sum')}</option>
                      <option value="min">{t('dashboard.widgetWizard.step2.metricValues.min')}</option>
                      <option value="max">{t('dashboard.widgetWizard.step2.metricValues.max')}</option>
                      <option value="count">{t('dashboard.widgetWizard.step2.metricValues.count')}</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Line Chart Table Configuration: Time Column, Y-axis Field, Entity Column */}
              {/* Config is now at root level (not per-table nested) */}
              {isLineChart && tableSources.length > 0 && (() => {
                // Get columns from all selected tables (local getTimestampColumns expects table name)
                const allTimestampColumns = [];
                const allValueColumns = [];
                tableSources.forEach((ts) => {
                  const tsCols = columnsByTable[ts.id] || [];
                  allTimestampColumns.push(...getTimestampColumnsByTable(ts.id));
                  allValueColumns.push(...getValueColumns(tsCols));
                });

                return (
                  <div className={styles.tableConfigSection}>
                    <h4 className={styles.tableConfigTitle}>
                      {tableSources.map((ts) => ts.id).join(', ')}
                    </h4>

                    {/* Time Column */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="timeColumn">
                        {t('dashboard.widgetWizard.step3.timeColumn')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.timeColumnHelp')}
                      </div>
                      <select
                        id="timeColumn"
                        className={styles.select}
                        value={dsConfig.timeColumn || ''}
                        onChange={(e) => handleDataSourceConfigChange('timeColumn', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.selectTimeColumn')}</option>
                        {allTimestampColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Y-axis Field */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="yAxisField">
                        {t('dashboard.widgetWizard.step3.yAxisField')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.yAxisFieldHelp')}
                      </div>
                      <select
                        id="yAxisField"
                        className={styles.select}
                        value={dsConfig.yAxisField || ''}
                        onChange={(e) => handleDataSourceConfigChange('yAxisField', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.selectYAxisField')}</option>
                        {allValueColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Group By - for multi-line charts */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="groupBy">
                        {t('dashboard.widgetWizard.step3.groupBy')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.lineChartGroupByHelp')}
                      </div>
                      <select
                        id="groupBy"
                        className={styles.select}
                        value={dsConfig.groupBy || ''}
                        onChange={(e) => handleDataSourceConfigChange('groupBy', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.noGroupBySingleLine')}</option>
                        {allValueColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })()}

              {/* Line Chart Custom Query Configuration: same Time Column / Y-axis / Group By from query result columns */}
              {isLineChart && hasCustomQuery && availableColumns && availableColumns.length > 0 && (() => {
                const allTimestampColumns = getTimestampColumns(availableColumns);
                const allValueColumns = getValueColumns(availableColumns);

                return (
                  <div className={styles.tableConfigSection}>
                    <h4 className={styles.tableConfigTitle}>
                      {t('dashboard.widgetWizard.dataSource.customQuery')}
                    </h4>

                    {/* Time Column */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="timeColumnCustom">
                        {t('dashboard.widgetWizard.step3.timeColumn')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.timeColumnHelp')}
                      </div>
                      <select
                        id="timeColumnCustom"
                        className={styles.select}
                        value={dsConfig.timeColumn || ''}
                        onChange={(e) => handleDataSourceConfigChange('timeColumn', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.selectTimeColumn')}</option>
                        {allTimestampColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                      {allTimestampColumns.length === 0 && (
                        <p className={styles.warningText}>
                          {t('dashboard.widgetWizard.step2.lineChartTimestampRequired')}
                        </p>
                      )}
                    </div>

                    {/* Y-axis Field */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="yAxisFieldCustom">
                        {t('dashboard.widgetWizard.step3.yAxisField')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.yAxisFieldHelp')}
                      </div>
                      <select
                        id="yAxisFieldCustom"
                        className={styles.select}
                        value={dsConfig.yAxisField || ''}
                        onChange={(e) => handleDataSourceConfigChange('yAxisField', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.selectYAxisField')}</option>
                        {allValueColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Group By - for multi-line charts */}
                    <div className={styles.configField}>
                      <label className={styles.fieldLabel} htmlFor="groupByCustom">
                        {t('dashboard.widgetWizard.step3.groupBy')}
                      </label>
                      <div className={styles.fieldDescription}>
                        {t('dashboard.widgetWizard.step3.lineChartGroupByHelp')}
                      </div>
                      <select
                        id="groupByCustom"
                        className={styles.select}
                        value={dsConfig.groupBy || ''}
                        onChange={(e) => handleDataSourceConfigChange('groupBy', e.target.value, activeSourceType)}
                      >
                        <option value="">{t('dashboard.widgetWizard.step3.noGroupBySingleLine')}</option>
                        {allValueColumns.map((col) => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })()}

              {/* Time Grouping - show when groupBy is a timestamp column */}
              {showGroupBy && dsConfig.groupBy && isGroupByTimestamp() && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="timestampPivot">
                    {t('dashboard.widgetWizard.step3.timestampPivot')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.timestampPivotHelp')}</div>
                  <select
                    id="timestampPivot"
                    className={styles.select}
                    value={dsConfig.timestampPivot || 'minute'}
                    onChange={(e) => handleDataSourceConfigChange('timestampPivot', e.target.value, activeSourceType)}
                  >
                    <option value="second">{t('dashboard.widgetWizard.step3.second')}</option>
                    <option value="minute">{t('dashboard.widgetWizard.step3.minute')}</option>
                    <option value="hour">{t('dashboard.widgetWizard.step3.hour')}</option>
                    <option value="hour_minute_24">{t('dashboard.widgetWizard.step3.hourMinute24')}</option>
                    <option value="hour_minute_12">{t('dashboard.widgetWizard.step3.hourMinute12')}</option>
                    <option value="day">{t('dashboard.widgetWizard.step3.day')}</option>
                    <option value="day_of_week">{t('dashboard.widgetWizard.step3.dayOfWeek')}</option>
                    <option value="day_of_year">{t('dashboard.widgetWizard.step3.dayOfYear')}</option>
                    <option value="day_of_month">{t('dashboard.widgetWizard.step3.dayOfMonth')}</option>
                    <option value="day_month">{t('dashboard.widgetWizard.step3.dayMonth')}</option>
                    <option value="month">{t('dashboard.widgetWizard.step3.month')}</option>
                    <option value="quarter">{t('dashboard.widgetWizard.step3.quarter')}</option>
                    <option value="year">{t('dashboard.widgetWizard.step3.year')}</option>
                    <option value="year_month">{t('dashboard.widgetWizard.step3.yearMonth')}</option>
                    <option value="year_quarter">{t('dashboard.widgetWizard.step3.yearQuarter')}</option>
                    <option value="year_month_day">{t('dashboard.widgetWizard.step3.yearMonthDay')}</option>
                  </select>
                </div>
              )}

              {/* Aggregation Type - for non-line charts only */}
              {showAggregation && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="aggregationType">
                    {t('dashboard.widgetWizard.step3.aggregationType')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.aggregationHelp')}</div>
                  <select
                    id="aggregationType"
                    className={styles.select}
                    value={dsConfig.aggregation || 'count'}
                    onChange={(e) => handleDataSourceConfigChange('aggregation', e.target.value, activeSourceType)}
                  >
                    <option value="count">{t('dashboard.widgetWizard.step3.count')}</option>
                    <option value="count_distinct">{t('dashboard.widgetWizard.step3.countDistinct')}</option>
                    <option value="sum">{t('dashboard.widgetWizard.step3.sum')}</option>
                    <option value="avg">{t('dashboard.widgetWizard.step3.average')}</option>
                    <option value="min">{t('dashboard.widgetWizard.step3.minimum')}</option>
                    <option value="max">{t('dashboard.widgetWizard.step3.maximum')}</option>
                  </select>
                </div>
              )}

              {/* Value Column - visible when aggregation needs a target column */}
              {showValueColumn && ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'].includes(dsConfig.aggregation) && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="valueColumn">
                    {t('dashboard.widgetWizard.step3.valueColumn')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.valueColumnHelp')}</div>
                  <select
                    id="valueColumn"
                    className={styles.select}
                    value={dsConfig.valueColumn || ''}
                    onChange={(e) => handleDataSourceConfigChange('valueColumn', e.target.value, activeSourceType)}
                  >
                    <option value="">{t('dashboard.widgetWizard.step3.selectColumn')}</option>
                    {availableColumns?.map((col) => (
                      <option key={col.name} value={col.name}>
                        {col.name} ({col.type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Display Name - for KPI only */}
              {showDisplayName && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="displayName">
                    {t('dashboard.widgetWizard.step3.displayName')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.displayNameHelp')}</div>
                  <input
                    id="displayName"
                    type="text"
                    className={styles.input}
                    value={config.displayName || ''}
                    onChange={(e) => onConfigChange('displayName', e.target.value)}
                    placeholder={t('dashboard.widgetWizard.step3.displayNamePlaceholder')}
                  />
                </div>
              )}

              {/* Category Limit */}
              {showCategoryLimit && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="categoryLimit">
                    {t('dashboard.widgetWizard.step3.categoryLimit')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.categoryLimitHelp')}</div>
                  <input
                    id="categoryLimit"
                    type="number"
                    className={styles.input}
                    value={dsConfig.categoryLimit || ''}
                    onChange={(e) =>
                      handleDataSourceConfigChange('categoryLimit', e.target.value ? parseInt(e.target.value, 10) : '', activeSourceType)
                    }
                    placeholder={t('dashboard.widgetWizard.step3.noLimit')}
                    min="1"
                    max="100"
                  />
                </div>
              )}

              {/* Table Columns - for table widget */}
              {showSelectedColumns && (
                <div className={styles.configField}>
                  <label className={styles.fieldLabel} htmlFor="tableColumns">
                    {t('dashboard.widgetWizard.step3.tableColumns')}
                  </label>
                  <div className={styles.fieldDescription}>{t('dashboard.widgetWizard.step3.tableColumnsHelp')}</div>
                  <SelectMulti
                    options={columnOptions}
                    value={dsConfig.selectedColumns || []}
                    onChange={handleColumnsChange}
                    placeholder={t('dashboard.widgetWizard.step3.selectColumnsPlaceholder')}
                    isDisabled={!availableColumns || availableColumns.length === 0}
                    noOptionsMessage={() => t('dashboard.widgetWizard.step3.noColumnsAvailable')}
                  />
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

Step3ChartConfig.propTypes = {
  widgetData: PropTypes.shape({
    type: PropTypes.string,
    config: PropTypes.shape({
      dataSources: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string,
          config: PropTypes.shape({}),
        }),
      ),
      displayName: PropTypes.string,
    }),
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onDataSourceConfigChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    chartBy: PropTypes.string,
    groupBy: PropTypes.string,
    aggregation: PropTypes.string,
  }).isRequired,
  availableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
};

Step3ChartConfig.defaultProps = {
  availableColumns: [],
};

export default Step3ChartConfig;
